import type { I18n, Locale } from '@/lib/api/types';

/** Resolve a bilingual field to the UI locale, falling back to EN (spec §5.3). */
export function resolveI18n(field: I18n | undefined, locale: Locale): string {
  if (!field) return '';
  const value = locale === 'ar' ? field.ar : field.en;
  return value || field.en || '';
}

export function isI18n(v: unknown): v is I18n {
  return (
    !!v &&
    typeof v === 'object' &&
    'en' in v &&
    'ar' in v &&
    // An I18n leaf has a string `en`. Nodes that merely happen to carry `en`/`ar`
    // keys with nested objects aren't leaves — recurse into them instead.
    typeof (v as Record<string, unknown>).en === 'string'
  );
}

/**
 * Walk an arbitrary value (entity, doc subtree) and report bilingual completeness by
 * counting I18n leaves that are missing their `ar` side (spec §6.2). Powers the per-item
 * "AR incomplete" status and the "missing Arabic" filter.
 */
export function arCompleteness(value: unknown): { total: number; missing: number } {
  let total = 0;
  let missing = 0;
  const visit = (node: unknown): void => {
    if (isI18n(node)) {
      // Only count fields that actually have EN content to translate.
      if (node.en && node.en.trim()) {
        total += 1;
        if (typeof node.ar !== 'string' || !node.ar.trim()) missing += 1;
      }
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(visit);
    } else if (node && typeof node === 'object') {
      Object.values(node as Record<string, unknown>).forEach(visit);
    }
  };
  visit(value);
  return { total, missing };
}

export function isArComplete(value: unknown): boolean {
  return arCompleteness(value).missing === 0;
}
